import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Tabs, Input, Flex, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import useDataList from '@/hooks/useDataList';
import apis, { QuotationRequestInts } from "@/apis/requirement";
import { CommonListParams } from '@/types/common';
import { QuotationRequest, QuotationRequestStatusEnum } from '@/types/requirement';
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from '@/styles/STableLayout';
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import SText from '@/styles/SText';
import { getSupplierName } from '@/utils/supplier';
import { dateFormat } from '@/utils/format';
import dayjs from 'dayjs';
import LazyQuate from './LazyQuate';
import QuotationForm from '@/components/QuotationForm';
import { getQuotationPrice, numFormmater } from '@/pages/Requirement/util';


interface ExportExcelButtonProps {
    requirementId: string;
}

const RequirementQuotation: React.FC<ExportExcelButtonProps> = ({ requirementId }) => {
    const { t } = useTranslation();
    const tableRef = useRef<TableLayoutRef | null>(null);

    const [visible, setVisible] = useState(false);
    const [keyword, setKeyword] = useState<string>("");
    const [activeTab, setActiveTab] = useState<QuotationRequestInts>(QuotationRequestInts.MANUAL);
    const [qutationList, setQutationList] = useState<QuotationRequest[]>([])

    const [qutation, setQutation] = useState<QuotationRequest | null>(null);
    const [detailId, setDetailId] = useState<string>("");


    const [, total, listLoading, { fetchList: getAllQuotation }] =
        useDataList<QuotationRequest, [string, CommonListParams]>({ fetchApi: apis.fetchAllQuotationRequests });

    const fetchList = (offset: number, keyword: string, ints: Array<number>) => {
        const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
        getAllQuotation(requirementId, {
            body: keyword,
            pagination: { offset, take },
            ints,
        }).then(({ body }) => {
            setQutationList(body ?? []);
        });
    };
    const handleCancel = () => {
        setVisible(false);
    };

    const lazyQuotaCb = (newQuotation: QuotationRequest | null) => {
        if (newQuotation) {
            setQutationList(last => {
                return last.map(item => {
                    if (item.id === newQuotation.id) return { ...item, deadline: newQuotation.deadline }
                    else return { ...item }
                })
            });
        }
        setQutation(null)
        return
    }

    const tableTabs = [
        {
            key: QuotationRequestInts.MANUAL + '',
            label: t("requirement.requirementHelp"),
        },
        {
            key: QuotationRequestInts.AUTO + '',
            label: t("requirement.supplierQuote"),
        },
    ];

    const headerChildren = <Flex justify='space-between' style={{ width: '100%' }}>
        <Tabs
            onChange={(tab) => setActiveTab(Number(tab) as QuotationRequestInts)}
            type="card"
            items={tableTabs}
        />
        <Input.Search
            enterButton
            placeholder={t("requirement.searchPlaceHolder")}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onSearch={(e) => {
                fetchList(0, e, [activeTab]);
                tableRef?.current?.resetTablePage();
            }}
        />
    </Flex>

    const getStatusLabel = (status: QuotationRequestStatusEnum) => {
        return status === QuotationRequestStatusEnum.READ ||
            status === QuotationRequestStatusEnum.UNREAD ? (
            <Tag color="#2db7f5">{t("requirement.unPriced")}</Tag>
        ) : status === QuotationRequestStatusEnum.SUBMITED ? (
            <Tag color="#87d068">{t("requirement.resolved")}</Tag>
        ) : status === QuotationRequestStatusEnum.REJECTED ? (
            <Tag color="#f50">{t("requirement.reject")}</Tag>
        ) : (
            <></>
        );
    };

    const tabTableColums: ColumnsType<QuotationRequest> = useMemo(() => {
        return [
            {
                title: t("purchase.code"),
                dataIndex: "code",
                width: 120,
                fixed: 'left',
                render: (_, item) => <SText text={item?.supplier?.code} style={{ width: "100%" }} />,
            },
            {
                title: t("requirement.supplier"),
                dataIndex: "supplerId",
                width:120,
                render: (_, record) => (
                    <SText
                        text={getSupplierName(record.supplier)}
                        style={{ width: "100%" }}
                    />
                ),
            },
            {
                title: t("requirement.supplierTime"),
                dataIndex: "mrd",
                width:120,
                render: (_, record) => (
                    <SText
                        text={
                            record?.supplierTime ? dateFormat(dayjs(record.supplierTime)) : ""
                        }
                        style={{ width: "100%" }}
                    />
                ),
            },
            {
                title: t("purchase.purPrice"),
                dataIndex: "price",
                width: 120,
                render: (_, record) => {
                    const price = getQuotationPrice(record);
                    return <SText text={price === null ? "" : numFormmater(price)} style={{ width: "100%" }} />;
                },
            },
            {
                title: t("purchase.productInfo"),
                children: [
                    {
                        title: <SText text={t("product.code")} />,
                        dataIndex: "code",
                        width: 100,
                        render: (_, item) => <SText text={item?.requirementItem?.product?.code ?? ''} style={{ width: "100%" }} />,
                    },
                    {
                        title: <SText text={t("product.name")} />,
                        dataIndex: "name",
                        width: 100,
                        render: (_, item) => <SText text={item?.requirementItem?.product?.name ?? ''} style={{ width: "100%" }} />,
                    },
                    {
                        title: <SText text={t("product.brand1")} />,
                        dataIndex: "model1",
                        width: 120,
                        render: (_, item) => <SText text={item?.requirementItem?.product?.model ?? ''} style={{ width: "100%" }} />,
                    },
                    {
                        title: <SText text={t("product.brand2")} />,
                        dataIndex: "model2",
                        width: 120,
                        render: (_, item) => <SText text={item?.requirementItem?.product?.t1 ?? ''} style={{ width: "100%" }} />,
                    },
                    {
                        title: <SText text={t("product.uom")} />,
                        dataIndex: "uom",
                        width: 80,
                        render: (_, item) => <SText text={item?.requirementItem?.product?.uom ?? ''} style={{ width: "100%" }} />,
                    },
                    {
                        title: <SText text={t("common.desc")} />,
                        dataIndex: "desc",
                        width: 120,
                        render: (_, item) => <SText text={item?.requirementItem?.product?.desc ?? ''} style={{ width: "100%" }} />,
                    },
                ]
            },
            {
                title: t("requirement.mpq"),
                dataIndex: "mpq",
                width:100,
                render: (_, record) => (
                    <SText text={record.mpq || ""} style={{ width: "100%" }} />
                ),
            },
            {
                title: t("requirement.days"),
                dataIndex: "days",
                width:120,
                render: (_, record) => (
                    <SText
                        text={record?.days?.toString() || ""}
                        style={{ width: "100%" }}
                    />
                ),
            },
            {
                title: t("requirement.status"),
                dataIndex: "status",
                width:100,
                fixed:'right',
                render: (_, record) => getStatusLabel(record.status)
            },
            {
                title: t("common.opt"),
                width:100,
                fixed:'right',
                render: (_, record) => (
                    record.status === QuotationRequestStatusEnum.SUBMITED || record.status === QuotationRequestStatusEnum.REJECTED ? null
                        : activeTab === QuotationRequestInts.AUTO ? <Button onClick={() => setQutation(record)}>
                            {t("requirement.lazyQuota")}
                        </Button>
                            : <Button onClick={() => setDetailId(record.id)}>
                                {t("requirement.edit")}
                            </Button>
                ),
            },
        ];
    }, [activeTab]);

    useEffect(() => {
        if (visible && requirementId) fetchList(0, '', [activeTab])
    }, [visible, requirementId, activeTab])

    return (
        <>
            <Button type="dashed" onClick={() => setVisible(true)}>{t('requirement.allQuotation')}</Button>
            <Modal
                title={t('requirement.allQuotation')}
                open={visible}
                footer={null}
                onCancel={handleCancel}
                width={'60vw'}
            >
                <STableLayout
                    loading={listLoading}
                    dataSource={qutationList}
                    rowKey="id"
                    headerChildren={headerChildren}
                    columns={tabTableColums as ColumnsType<AnyObject>}
                    size='small'
                    tableLayoutRef={tableRef}
                    total={total}
                    handleChange={(offset) => {
                        fetchList(offset, keyword, [activeTab]);
                    }}
                    scroll={{
                        x: 1200,
                        y: 350,
                        scrollToFirstRowOnChange: true
                    }}
                />

            </Modal>
            {qutation && <LazyQuate quotation={qutation} cb={(newQutation) => lazyQuotaCb(newQutation)} />}
            {detailId && <QuotationForm
                formKey={"cooperate"}
                detailId={detailId}
                onCancel={() => setDetailId("")}
                onSubmit={() => {
                    setQutationList(last => {
                        return last.map(item => {
                            if (item.id === detailId) return { ...item, status: QuotationRequestStatusEnum.SUBMITED }
                            else return { ...item }
                        })
                    });
                }}
                onReject={() => {
                    setQutationList(last => {
                        return last.map(item => {
                            if (item.id === detailId) return { ...item, status: QuotationRequestStatusEnum.REJECTED }
                            else return { ...item }
                        })
                    });
                }}
            />}
        </>
    );
};

export default RequirementQuotation;
